import { getUserProfile } from "../../redux/selectors/profileSelectors";
import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Select, DatePicker, Spin } from "antd";
import {
  PlusOutlined,
  FilePdfOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { searchDepartments, searchTalents, createProject, fetchProjectDetail, updateProject } from "../../redux/actions";
import { uploadFileApi, triggerAfterUpload } from "../../API/API";
import moment from "moment";
import "./CreateProject.css";

const { Option } = Select;
const { TextArea } = Input;



function CreateProject() {
  const history = useHistory();
  const { id } = useParams();
  const isEdit = !!id;
  const dispatch = useDispatch();
  const deptDebounceRef = useRef(null);
  const talentDebounceRef = useRef(null);
  const selectedTalentsRef = useRef({});
  const hasSubmitted = useRef(false);

  const { departments, loading: deptLoading } = useSelector(
    (state) => state.departmentSearchReducer
  );

  const currencyCodeFromStore = useSelector(
    (state) => state.profileReducer?.user?.profile?.Countries?.CurrencyCode
  );
  const currencyCode = currencyCodeFromStore ||
    (() => { try { return getUserProfile()?.Profiles?.Countries?.CurrencyCode || ""; } catch { return ""; } })();
  const { talents, loading: talentLoading } = useSelector(
    (state) => state.talentSearchReducer
  );
  const { loading: creating, success: created } = useSelector(
    (state) => state.createProjectReducer
  );
  const { loading: updating, success: updated } = useSelector(
    (state) => state.updateProjectReducer
  );
  const { project: projectDetail, loading: detailLoading } = useSelector(
    (state) => state.projectDetailReducer
  );

  const [form, setForm] = useState({
    projectName: "",
    description: "",
    managerAccountIds: [],
    departmentId: "",
    departmentName: "",
    budget: "",
    startDate: null,
    dueDate: null,
    priority: "",
    status: "",
  });

  const [errors, setErrors] = useState({});

  const validate = (f) => {
    const e = {};
    if (!f.projectName.trim()) e.projectName = "Project name is required";
    if (!f.description.trim()) e.description = "Description is required";
    if (!f.managerAccountIds.length) e.managerAccountIds = "Project manager is required";
    if (!f.departmentId) e.departmentId = "Department is required";
    if (!f.startDate) e.startDate = "Start date is required";
    if (!f.priority) e.priority = "Priority is required";
    if (!f.status) e.status = "Status is required";
    return e;
  };

  const clearError = (field) =>
    setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });

  useEffect(() => {
    if (isEdit && id) dispatch(fetchProjectDetail(id));
  }, [id]);

  useEffect(() => {
    if (isEdit && projectDetail) {
      const managers = projectDetail.projectManagers || [];
      // pre-populate selectedTalentsRef so tags show names
      const map = {};
      managers.forEach((m) => {
        map[m.accountId] = {
          accountId: m.accountId,
          FullName: m.name || m.email,
          EmailAddress: m.email,
        };
      });
      selectedTalentsRef.current = map;

      const existingDoc = projectDetail.documents?.[0];
      if (existingDoc?.s3FileKey) {
        const fileName = existingDoc.s3FileKey.split("/").pop();
        setUploadedFile({ name: fileName });
        setUploadedDoc({ s3FileKey: existingDoc.s3FileKey });
      } else {
        setUploadedFile(null);
        setUploadedDoc(null);
      }

      setForm({
        projectName: projectDetail.projectName || "",
        description: projectDetail.projectDescription || "",
        managerAccountIds: managers.map((m) => m.accountId).filter(Boolean),
        departmentId: projectDetail.department?.departmentId || "",
        departmentName: projectDetail.department?.departmentName || "",
        budget: projectDetail.budget?.Amount ? String(projectDetail.budget.Amount) : "",
        startDate: projectDetail.startDate ? moment(projectDetail.startDate) : null,
        dueDate: projectDetail.dueDate ? moment(projectDetail.dueDate) : null,
        priority: projectDetail.priority || "",
        status: projectDetail.status || "",
      });
    }
  }, [projectDetail]);

  useEffect(() => {
    if (hasSubmitted.current && (created || updated)) {
      history.push("/manage-projects");
    }
  }, [created, updated]);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedDoc, setUploadedDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDepartmentSearch = (value) => {
    clearTimeout(deptDebounceRef.current);
    deptDebounceRef.current = setTimeout(() => {
      if (value) dispatch(searchDepartments(value));
    }, 400);
  };

  const handleTalentSearch = (value) => {
    clearTimeout(talentDebounceRef.current);
    talentDebounceRef.current = setTimeout(() => {
      dispatch(searchTalents(value || ""));
    }, 300);
  };

  const handleTalentDropdownOpen = (open) => {
    if (open && (talents || []).length === 0) {
      dispatch(searchTalents(""));
    }
  };

  const handleTalentChange = (values) => {
    const updatedMap = {};
    values.forEach((id) => {
      if (selectedTalentsRef.current[id]) {
        updatedMap[id] = selectedTalentsRef.current[id];
      } else {
        const found = (talents || []).find((t) => t.accountId === id);
        if (found) updatedMap[id] = found;
      }
    });
    selectedTalentsRef.current = updatedMap;
    setForm((f) => ({ ...f, managerAccountIds: values }));
    clearError("managerAccountIds");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedFile(file);
    setUploading(true);
    try {
      const result = await uploadFileApi({ params: { file, type: "projectDocument" } });
      if (result?.data?.status === "success") {
        const s3Key = result.data.payload;
        setUploadedDoc({ s3FileKey: s3Key });
        try { await triggerAfterUpload(s3Key); } catch (_) {}
      }
    } catch (err) {
      setUploadedFile(null);
      setUploadedDoc(null);
    }
    setUploading(false);
  };

  const handleSubmit = () => {
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    hasSubmitted.current = true;
    const payload = {
      ProjectName: form.projectName,
      Description: form.description,
      ManagerAccountIds: form.managerAccountIds,
      DepartmentId: form.departmentId,
      DepartmentName: form.departmentName,
      ...(form.budget && {
        Budget: { Amount: parseFloat(form.budget), Currency: "PKR" },
      }),
      ...(form.startDate && { StartDate: form.startDate.format("YYYY-MM-DD") }),
      ...(form.dueDate && { DueDate: form.dueDate.format("YYYY-MM-DD") }),
      Priority: form.priority,
      Status: form.status,
      Documents: uploadedDoc ? [uploadedDoc] : [],
    };
    if (isEdit) {
      dispatch(updateProject(id, payload));
    } else {
      dispatch(createProject(payload));
    }
  };

  return (
    <div className="cp-page">
      <button className="cp-back-btn" onClick={() => history.push("/manage-projects")}>
        <ArrowLeftOutlined />
      </button>

      <div className="cp-card">
        <div className="cp-card-header">
          <h2 className="cp-title">{isEdit ? "Edit Project" : "Create New Project"}</h2>
        </div>

        <Spin spinning={isEdit && detailLoading} size="large">
        <div className="cp-form-body">
          <div className="cp-field-group">
            <label className="cp-label">
              Project Name <span className="cp-required">*</span>
            </label>
            <Input
              placeholder="Enter project name"
              className="cp-input"
              status={errors.projectName ? "error" : ""}
              value={form.projectName}
              onChange={(e) => { setForm((f) => ({ ...f, projectName: e.target.value })); clearError("projectName"); }}
            />
            {errors.projectName && <span className="cp-error-msg">{errors.projectName}</span>}
          </div>

          <div className="cp-field-group">
            <label className="cp-label">
              Description <span className="cp-required">*</span>
            </label>
            <TextArea
              placeholder="Enter description"
              className="cp-textarea"
              autoSize={{ minRows: 4 }}
              maxLength={5000}
              showCount
              status={errors.description ? "error" : ""}
              value={form.description}
              onChange={(e) => { setForm((f) => ({ ...f, description: e.target.value })); clearError("description"); }}
            />
            {errors.description && <span className="cp-error-msg">{errors.description}</span>}
          </div>

          <div className="cp-field-row">
            <div className="cp-field-group cp-half">
              <label className="cp-label">
                Project Manager <span className="cp-required">*</span>
              </label>
              {(() => {
                const selectedObjs = Object.values(selectedTalentsRef.current);
                const selectedIds = new Set(form.managerAccountIds);
                const apiTalents = (Array.isArray(talents) ? talents : []).filter(
                  (t) => !selectedIds.has(t.accountId)
                );
                const mergedOptions = [...selectedObjs, ...apiTalents];
                return (
                  <Select
                    mode="multiple"
                    placeholder="Select"
                    className="cp-select cp-select-multi"
                    showSearch
                    filterOption={false}
                    onSearch={handleTalentSearch}
                    onDropdownVisibleChange={handleTalentDropdownOpen}
                    loading={talentLoading}
                    notFoundContent={talentLoading ? "Searching..." : "No results found"}
                    value={form.managerAccountIds}
                    onChange={handleTalentChange}
                    status={errors.managerAccountIds ? "error" : ""}
                    tagRender={({ value, onClose }) => {
                      const t = mergedOptions.find((o) => o.accountId === value);
                      const name = t?.FullName || t?.EmailAddress || value;
                      const role = t?.professionalRoleName;
                      return (
                        <span className="ant-select-selection-item" style={{ display: 'flex', flexDirection: 'column', height: 'auto', lineHeight: '1.3', padding: '2px 6px', margin: '2px' }}>
                          <span style={{ fontWeight: 500 }}>{name}</span>
                          {role && <span style={{ fontSize: '11px', color: '#888' }}>({role})</span>}
                          <span onClick={onClose} style={{ position: 'absolute', right: '4px', top: '4px', cursor: 'pointer', fontSize: '10px' }}>✕</span>
                        </span>
                      );
                    }}
                  >
                    {mergedOptions.map((t) => (
                      <Option key={t.accountId} value={t.accountId}>
                        <div className="cp-talent-option">
                          <span className="cp-talent-name">{t.FullName}</span>
                          {t.professionalRoleName && <span style={{ fontSize: '11px', color: '#888', display: 'block' }}>({t.professionalRoleName})</span>}
                          <span className="cp-talent-email">{t.EmailAddress}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                );
              })()}
              {errors.managerAccountIds && <span className="cp-error-msg">{errors.managerAccountIds}</span>}
            </div>
            <div className="cp-field-group cp-half">
              <label className="cp-label">
                Department <span className="cp-required">*</span>
              </label>
              <Select
                placeholder="Select"
                className="cp-select"
                value={form.departmentId || undefined}
                showSearch
                filterOption={false}
                onSearch={handleDepartmentSearch}
                loading={deptLoading}
                notFoundContent={deptLoading ? "Searching..." : "No departments found"}
                status={errors.departmentId ? "error" : ""}
                onChange={(value, option) => {
                  setForm((f) => ({
                    ...f,
                    departmentId: value,
                    departmentName: option?.children || "",
                  }));
                  clearError("departmentId");
                }}
              >
                {form.departmentId && form.departmentName &&
                  !(Array.isArray(departments) ? departments : []).find(
                    (d) => d._id === form.departmentId
                  ) && (
                  <Option key={form.departmentId} value={form.departmentId}>
                    {form.departmentName}
                  </Option>
                )}
                {(Array.isArray(departments) ? departments : []).map((dept) => (
                  <Option key={dept._id} value={dept._id}>
                    {dept.DepartmentName}
                  </Option>
                ))}
              </Select>
              {errors.departmentId && <span className="cp-error-msg">{errors.departmentId}</span>}
            </div>
          </div>

          <div className="cp-field-group">
            <label className="cp-label">Project Budget</label>
            <Input
              prefix={<span style={{ color: '#606F7E', fontWeight: 500, fontSize: '13px' }}>PKR</span>}
              placeholder="Enter Budget"
              className="cp-input"
              value={form.budget}
              onChange={(e) => { const v = e.target.value; if (/^\d*$/.test(v)) setForm((f) => ({ ...f, budget: v })); }}
            />
          </div>

          <div className="cp-field-row">
            <div className="cp-field-group cp-half">
              <label className="cp-label">
                Start Date <span className="cp-required">*</span>
              </label>
              <DatePicker
                className="cp-datepicker"
                placeholder="Select"
                format="DD MMM YYYY"
                value={form.startDate}
                status={errors.startDate ? "error" : ""}
                onChange={(date) => { setForm((f) => ({ ...f, startDate: date })); clearError("startDate"); }}
              />
              {errors.startDate && <span className="cp-error-msg">{errors.startDate}</span>}
            </div>
            <div className="cp-field-group cp-half">
              <label className="cp-label">Due Date</label>
              <DatePicker
                className="cp-datepicker"
                placeholder="Select"
                format="DD MMM YYYY"
                value={form.dueDate}
                onChange={(date) => setForm((f) => ({ ...f, dueDate: date }))}
              />
            </div>
          </div>

          <div className="cp-field-row">
            <div className="cp-field-group cp-half">
              <label className="cp-label">
                Priority <span className="cp-required">*</span>
              </label>
              <Select
                placeholder="Select"
                className="cp-select"
                value={form.priority || undefined}
                status={errors.priority ? "error" : ""}
                onChange={(value) => { setForm((f) => ({ ...f, priority: value })); clearError("priority"); }}
              >
                <Option value="high">High</Option>
                <Option value="medium">Medium</Option>
                <Option value="low">Low</Option>
              </Select>
              {errors.priority && <span className="cp-error-msg">{errors.priority}</span>}
            </div>
            <div className="cp-field-group cp-half">
              <label className="cp-label">
                Status <span className="cp-required">*</span>
              </label>
              <Select
                placeholder="Select"
                className="cp-select"
                value={form.status || undefined}
                status={errors.status ? "error" : ""}
                onChange={(value) => { setForm((f) => ({ ...f, status: value })); clearError("status"); }}
              >
                <Option value="new">New</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="on_hold">On Hold</Option>
                <Option value="completed">Completed</Option>
                <Option value="overdue">Overdue</Option>
                <Option value="draft">Draft</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
              {errors.status && <span className="cp-error-msg">{errors.status}</span>}
            </div>
          </div>

          <div className="cp-field-group">
            <label className="cp-label">Upload Document</label>
            <div
              className="cp-upload-area"
              onClick={() => document.getElementById("cp-file-input").click()}
            >
              {uploadedFile ? (
                <div className="cp-uploaded-file">
                  <FilePdfOutlined className="cp-pdf-icon" />
                  <div className="cp-file-info">
                    <span className="cp-file-name">{uploadedFile.name}</span>
                    {uploading && (
                      <span className="cp-file-size">Uploading...</span>
                    )}
                  </div>
                  <DeleteOutlined
                    className="cp-delete-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadedFile(null);
                      setUploadedDoc(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  />
                </div>
              ) : (
                <div className="cp-upload-placeholder">
                  <PlusOutlined className="cp-upload-plus" />
                  <span>Click or drag file to upload</span>
                </div>
              )}
            </div>
            <input
              id="cp-file-input"
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>

          <Button
            className="cp-submit-btn"
            loading={isEdit ? updating : creating}
            disabled={
              uploading ||
              !form.projectName.trim() ||
              !form.description.trim() ||
              !form.managerAccountIds.length ||
              !form.departmentId ||
              !form.startDate ||
              !form.priority ||
              !form.status
            }
            onClick={handleSubmit}
          >
            {isEdit ? (
              <><CheckOutlined style={{ marginRight: 6 }} /> Save changes</>
            ) : (
              "Create Project"
            )}
          </Button>
        </div>
        </Spin>
      </div>
    </div>
  );
}

export default CreateProject;
